import React from "react";
import theme from "../../../styles/theme";

/* ============================================================================
   SPECIFICATION MAP DETAILS PANEL
   ----------------------------------------------------------------------------
   VERSION STANDARDISÉE
   - grammaire strictement alignée sur le template fourni
   - toutes les valeurs passent par KeyValueRow
   - aucune pill décorative
   - aucune ligne de séparation
============================================================================ */

const T = {
  text: theme?.colors?.text?.primary || "#164D63",
  soft: theme?.colors?.text?.small || "#5C7A89",
  verySoft: theme?.colors?.text?.verysmall || "#7D97A1",
  border: theme?.colors?.borders?.default || "#d6dee4",

  blueText: theme?.Generic?.blue?.pastel?.text || "#1E5066",
  greenText: theme?.Generic?.green?.pastel?.text || "#166A58",
  yellowText: theme?.Generic?.yellow?.pastel?.text || "#8A4A13",
  orangeText: theme?.Generic?.orange?.pastel?.text || "#9A4315",
  greyText: theme?.Generic?.grey?.pastel?.text || "#3F5D6E",
  redText: theme?.Generic?.red?.pastel?.text || "#6E1F1F",
};

const S = {
  panel: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: 22,
    boxSizing: "border-box",
  },

  panelHeader: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    minHeight: 12,
  },

  panelTitle: {
    fontFamily: theme?.fonts?.body || "inherit",
    fontSize: "1.02rem",
    lineHeight: 1.1,
    color: T.text,
    fontWeight: 600,
  },

  block: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    paddingBottom: 2,
  },

  blockTitle: {
    fontFamily: theme?.fonts?.body || "inherit",
    fontSize: "0.84rem",
    lineHeight: 1.12,
    color: theme?.colors?.text?.caneva || "#c24c2c",
    fontWeight: 600,
  },

  rows: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },

  kvRow: {
    display: "grid",
    gridTemplateColumns: "1fr auto",
    alignItems: "center",
    gap: 12,
  },

  kvLabel: {
    fontFamily: theme?.fonts?.body || "inherit",
    fontSize: "0.74rem",
    lineHeight: 1.2,
    color: T.soft,
    minWidth: 0,
  },

  kvValue: {
    fontFamily: theme?.fonts?.body || "inherit",
    fontSize: "0.76rem",
    lineHeight: 1.2,
    fontWeight: 600,
    textAlign: "right",
    whiteSpace: "nowrap",
  },

  bodyText: {
    fontFamily: theme?.fonts?.body || "inherit",
    fontSize: "0.82rem",
    lineHeight: 1.45,
    color: T.text,
  },

  listBlock: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    alignItems: "flex-end",
  },

  listItem: {
    fontFamily: theme?.fonts?.body || "inherit",
    fontSize: "0.76rem",
    lineHeight: 1.2,
    fontWeight: 600,
    textAlign: "right",
    whiteSpace: "nowrap",
    color: T.blueText,
  },

  actionsWrap: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
  },

  actionButton: {
    height: 28,
    padding: "0 10px",
    borderRadius: theme?.borders?.radius?.md || 4,
    border: `1px solid ${T.border}`,
    background: "#ffffff",
    color: T.text,
    fontFamily: theme?.fonts?.body || "inherit",
    fontSize: "0.80rem",
    fontWeight: theme?.fontWeights?.medium || 500,
    lineHeight: 1,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },

  actionButtonPrimary: {
    border: `1px solid ${T.border}`,
    background: "#f7fafc",
    color: T.blueText,
  },

  empty: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: 22,
    boxSizing: "border-box",
  },

  emptyText: {
    fontFamily: theme?.fonts?.body || "inherit",
    fontSize: "0.78rem",
    lineHeight: 1.35,
    color: T.verySoft,
  },
};

/* ============================================================================
   HELPERS
============================================================================ */

const getMaturityColor = (value) => {
  const map = {
    "Non traitée": T.greyText,
    "En cours": T.blueText,
    "À consolider": T.yellowText,
    Validée: T.greenText,
  };

  return map[value] || T.text;
};

const getTechnicalColor = (value) => {
  const map = {
    "Non définie": T.greyText,
    Instable: T.orangeText,
    Partielle: T.yellowText,
    "Maîtrisée": T.greenText,
  };

  return map[value] || T.text;
};

const getImpactColor = (value) => {
  const map = {
    Bloquant: T.redText,
    Critique: T.orangeText,
    "Sous contrôle": T.blueText,
    Neutre: T.text,
  };

  return map[value] || T.text;
};

const buildDecisionReading = (item) => {
  if (!item) return "";
  return (
    item.synthesis ||
    "La lecture projet doit être complétée à partir des tensions, interfaces et niveaux de couverture identifiés."
  );
};

/* ============================================================================
   UI
============================================================================ */

const PanelBlock = ({ title, children }) => (
  <div style={S.block}>
    <div style={S.blockTitle}>{title}</div>
    {children}
  </div>
);

const KeyValueRow = ({ label, value, valueColor = T.text, valueStyle = {} }) => (
  <div style={S.kvRow}>
    <div style={S.kvLabel}>{label}</div>
    <div style={{ ...S.kvValue, color: valueColor, ...valueStyle }}>{value}</div>
  </div>
);

const EmptyState = () => (
  <div style={S.empty}>
    <div style={S.panelHeader}>
      <div style={S.panelTitle}>Lecture ciblée</div>
    </div>

    <PanelBlock title="Selected item">
      <div style={S.emptyText}>
        Sélectionnez une ligne dans la cartographie pour consulter son détail.
      </div>
    </PanelBlock>
  </div>
);

const ActionButton = ({ label, onClick, primary = false }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      ...S.actionButton,
      ...(primary ? S.actionButtonPrimary : {}),
    }}
  >
    {label}
  </button>
);

/* ============================================================================
   COMPONENT
============================================================================ */

const SpecificationMapDetailsPanel = ({
  selectedItem = null,
  onOpenSpecification,
  onMarkForReview,
}) => {
  if (!selectedItem) {
    return <EmptyState />;
  }

  const quickReading = buildDecisionReading(selectedItem);

  return (
    <div style={S.panel}>
      <div style={S.panelHeader}>
        <div style={S.panelTitle}>
          {selectedItem.label || "Élément sélectionné"}
        </div>
      </div>

      <PanelBlock title="Lecture projet">
        <div style={S.bodyText}>{quickReading}</div>
      </PanelBlock>

      <PanelBlock title="Identification">
        <div style={S.rows}>
          <KeyValueRow
            label="Élément"
            value={selectedItem.label || "—"}
            valueColor={T.blueText}
          />
          <KeyValueRow
            label="Discipline"
            value={selectedItem.discipline || "—"}
            valueColor={T.blueText}
          />
          <KeyValueRow
            label="Impact"
            value={selectedItem.projectImpact || "—"}
            valueColor={getImpactColor(selectedItem.projectImpact)}
          />
        </div>
      </PanelBlock>

      <PanelBlock title="Couverture">
        <div style={S.rows}>
          <KeyValueRow
            label="Maturité"
            value={selectedItem.maturityStatus || "—"}
            valueColor={getMaturityColor(selectedItem.maturityStatus)}
          />
          <KeyValueRow
            label="Technique"
            value={selectedItem.technicalStatus || "—"}
            valueColor={getTechnicalColor(selectedItem.technicalStatus)}
          />
        </div>
      </PanelBlock>

      <PanelBlock title="Objet de la spécification">
        {(selectedItem.objets || []).length > 0 ? (
          <div style={S.listBlock}>
            {selectedItem.objets.map((item) => (
              <div key={item} style={S.listItem}>
                {item}
              </div>
            ))}
          </div>
        ) : (
          <div style={S.listBlock}>
            <div style={S.listItem}>Aucun objet renseigné</div>
          </div>
        )}
      </PanelBlock>

      <PanelBlock title="Interfaces">
        {(selectedItem.interfaces || []).length > 0 ? (
          <div style={S.listBlock}>
            {selectedItem.interfaces.map((item) => (
              <div key={item} style={S.listItem}>
                {item}
              </div>
            ))}
          </div>
        ) : (
          <div style={S.listBlock}>
            <div style={S.listItem}>Aucune interface identifiée</div>
          </div>
        )}
      </PanelBlock>

      <PanelBlock title="Synthèse">
        <div style={S.rows}>
          <KeyValueRow
            label="Générées"
            value={String(selectedItem.specsGenerees ?? "—")}
            valueStyle={S.synthesisValue}
          />
          <KeyValueRow
            label="Sous tension"
            value={String(selectedItem.sousTension ?? "—")}
            valueStyle={S.synthesisValue}
          />
          <KeyValueRow
            label="Maîtrisées"
            value={String(selectedItem.maitrisees ?? "—")}
            valueStyle={S.synthesisValue}
          />
        </div>
      </PanelBlock>

      <PanelBlock title="Actions">
        <div style={S.actionsWrap}>
          <ActionButton
            label="Analyser"
            primary={true}
            onClick={() => onOpenSpecification?.(selectedItem)}
          />
          <ActionButton
            label="Marquer pour revue"
            onClick={() => onMarkForReview?.(selectedItem)}
          />
        </div>
      </PanelBlock>
    </div>
  );
};

export default SpecificationMapDetailsPanel;